import React, { ChangeEventHandler, FC, FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import styled from 'styled-components';

import {
  Autocomplete,
  Box,
  BoxProps,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { regions } from '@luxuryescapes/lib-regions/lib/regions';

import SearchForm from '~/components/Common/Forms/SearchForm';
import Spinner from '~/components/Common/Spinner';
import AddTrendingPlaceModal from '~/components/Content/TrendingPlaces/AddTrendingPlaceModal';
import { TrendingPlace } from '~/components/Content/TrendingPlaces/Columns';
import TrendingPlacesTable from '~/components/Content/TrendingPlaces/TrendingPlacesTable';
import UpdateTrendingPlaceImageModal from '~/components/Content/TrendingPlaces/UpadteTrendingPlaceImageModal';
import UpdateTrendingPlaceModal from '~/components/Content/TrendingPlaces/UpdateTrendingPlaceModal';

import SearchService from '~/services/SearchService';

const Toolbar = styled((props: BoxProps) => <Box component="div" {...props} />)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  @media (min-width: 1100px) {
    flex-wrap: nowrap;
  }
` as FC<BoxProps>;

const DEFAULT_BRAND = 'luxuryescapes';
const DEFAULT_REGION = 'AU';
export const TRENDING_PLACES_BRANDS = [
  { value: 'luxuryescapes', name: 'Luxury Escapes' },
  { value: 'scoopontravel', name: 'Scoopon Travel' },
  { value: 'cudotravel', name: 'Cudo Travel' },
  { value: 'treatmetravel', name: 'Treat Me Travel' },
  { value: 'dealstravel', name: 'Deals Travel' },
  { value: 'lebusinesstraveller', name: 'LE Business Traveller' },
  { value: 'leagenthub', name: 'LE Agent Hub' },
  { value: 'leagenthub_cvfr', name: 'CVFR Holidays' },
];

function TrendingPlacesPage() {
  const [brand, setBrand] = useState<string>(DEFAULT_BRAND);
  const [region, setRegion] = useState<string>(DEFAULT_REGION);
  const [searchString, setSearchString] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [trendingPlaces, setTrendingPlaces] = useState<Array<TrendingPlace>>([]);
  const [displayedTrendingPlaces, setDisplayedTrendingPlaces] = useState<Array<TrendingPlace>>([]);
  const [rowToUpdate, setRowToUpdate] = useState<TrendingPlace | undefined>(undefined);
  const [isUpdateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [isAddModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [isImageModalOpen, setImageModalOpen] = useState<boolean>(false);

  // Memoize the regions array to prevent unnecessary re-renders
  const availableRegions = useMemo(() => regions['luxuryescapes'], []);

  const handleSearchTextChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchString(e.target.value);
  };

  useEffect(() => {
    const enteredString = searchString.trim().toLowerCase();
    if (!enteredString) {
      setDisplayedTrendingPlaces(trendingPlaces); // Reset to original list if searchString is empty
    } else {
      setDisplayedTrendingPlaces(
        trendingPlaces.filter(
          (item) =>
            item.placeName.toLowerCase().includes(enteredString) || item.placeId.toLowerCase().includes(enteredString),
        ),
      );
    }
  }, [searchString, trendingPlaces]);

  const fetchData = useCallback(
    async ({ showLoading = true, clearOnError = true } = {}): Promise<boolean> => {
      if (showLoading) {
        setLoading(true);
      }
      try {
        const { result } = await SearchService.getTrendingPlace(brand, region);
        const fetchedData = result.map(
          (
            { id, placeId, manualPosition, zscore, imageId, primaryText, brand, region, enforcedPosition },
            index: number,
          ) => ({
            id,
            position: index + 1,
            manualPosition: manualPosition ?? 'Not Set',
            zscore: zscore ?? 'null',
            placeName: primaryText,
            placeId,
            imageId,
            brand,
            region,
            enforcedPosition: enforcedPosition ?? false,
          }),
        );
        setTrendingPlaces(fetchedData);
        setDisplayedTrendingPlaces(fetchedData);
        return true;
      } catch {
        if (clearOnError) {
          setTrendingPlaces([]);
        }
        return false;
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [brand, region],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, brand, region]);

  const handleSearchSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();
      setSearchString(searchString);
    },
    [searchString],
  );

  const handleUpdate = useCallback(async (row: TrendingPlace) => {
    setUpdateModalOpen(true);
    setRowToUpdate(row);
  }, []);

  const handleEnforcedPositionChange = useCallback(
    async (row: TrendingPlace, newValue: boolean) => {
      try {
        await SearchService.upsertTrendingPlace(
          row.placeId,
          row.brand,
          row.region,
          row.imageId,
          typeof row.manualPosition === 'number' ? row.manualPosition : undefined,
          newValue,
        );
        await fetchData();
      } catch (error) {
        console.error('Failed to update enforced position:', error);
      }
    },
    [fetchData],
  );

  const handleManualPositionChange = useCallback(
    async (row: TrendingPlace, manualPosition?: number | null): Promise<boolean> => {
      try {
        await SearchService.upsertTrendingPlace(
          row.placeId,
          row.brand,
          row.region,
          row.imageId,
          manualPosition,
          row.enforcedPosition,
        );
        return await fetchData({ showLoading: false, clearOnError: false });
      } catch (error) {
        console.error('Failed to update manual position:', error);
        return false;
      }
    },
    [fetchData],
  );

  const handleAddOpenModal = () => {
    setAddModalOpen(true);
  };

  const handleImageOpenModal = () => {
    setImageModalOpen(true);
  };

  const handleAddCloseModal = async () => {
    setAddModalOpen(false);
    await fetchData();
  };

  const handleImageCloseModal = async () => {
    setImageModalOpen(false);
    await fetchData();
  };

  const handleUpdateCloseModal = async () => {
    setUpdateModalOpen(false);
    await fetchData();
  };

  const onRegionChange = useCallback((_: React.SyntheticEvent, selectedRegion: (typeof availableRegions)[number]) => {
    setRegion(selectedRegion.code);
  }, []);

  const onBrandChange = useCallback((event: SelectChangeEvent) => {
    const brand = event.target.value;
    setBrand(brand);
  }, []);

  return (
    <Container>
      <Helmet>
        <title>Trending Places</title>
      </Helmet>

      <Stack direction="column" gap={4}>
        <AddTrendingPlaceModal open={isAddModalOpen} onClose={handleAddCloseModal} />
        <UpdateTrendingPlaceImageModal open={isImageModalOpen} onClose={handleImageCloseModal} />
        <UpdateTrendingPlaceModal open={isUpdateModalOpen} onClose={handleUpdateCloseModal} rowToUpdate={rowToUpdate} />

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Toolbar>
            <Autocomplete
              disableClearable
              options={availableRegions}
              value={availableRegions.find((regionOption) => regionOption.code === region) ?? null}
              onChange={onRegionChange}
              getOptionLabel={(regionOption) => regionOption.name}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderInput={(params) => <TextField {...params} label="Market" />}
              sx={{ flex: { xs: '1 1 100%', lg: '0 0 190px' } }}
            />

            <FormControl sx={{ flex: { xs: '1 1 100%', lg: '0 0 220px' } }}>
              <InputLabel id="brand-select-label">Brand</InputLabel>
              <Select
                labelId="brand-select-label"
                label="Brand"
                value={brand}
                onChange={onBrandChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {TRENDING_PLACES_BRANDS.map((brandOption) => (
                  <MenuItem key={brandOption.value} value={brandOption.value}>
                    {brandOption.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box flex={{ xs: '1 1 100%', lg: '1 1 280px' }} minWidth={0}>
              <SearchForm
                id="TrendingPlacesSearch"
                placeholder="Search by destination name or place ID"
                searchString={searchString}
                handleChange={handleSearchTextChange}
                handleSubmit={handleSearchSubmit}
                showSubmitButton={false}
              />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} flex="0 0 auto">
              <Button variant="outlined" onClick={handleImageOpenModal} sx={{ whiteSpace: 'nowrap' }}>
                Manage images
              </Button>
              <Button variant="contained" color="primary" onClick={handleAddOpenModal} sx={{ whiteSpace: 'nowrap' }}>
                Add destination
              </Button>
            </Stack>
          </Toolbar>
        </Paper>

        {isLoading && <Spinner />}

        <TrendingPlacesTable
          isLoading={isLoading}
          trendingPlaces={displayedTrendingPlaces}
          handleUpdate={handleUpdate}
          onEnforcedPositionChange={handleEnforcedPositionChange}
          onManualPositionChange={handleManualPositionChange}
        />

        <Typography align="right" color="text.secondary" variant="body2">
          {displayedTrendingPlaces.length} of {trendingPlaces.length} destinations shown
        </Typography>
      </Stack>
    </Container>
  );
}

export default TrendingPlacesPage;
